import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { HiEnvelope, HiTrash, HiKey } from 'react-icons/hi2';
import type { RootState } from '@/store/store';
import { isApiError } from '@/utils/errorUtils';
import { PageContent, PageHeader } from '@/components/layout/PageLayout';
import { Section, SectionTitle } from '@/components/layout/Toolbar.styled';
import { SurfaceCard, LoadingCenter } from '@/styles/shared.styled';
import { DataTable, type DataTableColumn } from '@/components/common/DataTable';
import type { OrgMemberDto } from '@/store/usersApi';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import StatusDefinitionsAdmin from '@/components/admin/StatusDefinitionsAdmin';
import {
  InviteForm,
  MemberAvatar,
  MemberAvatarFallback,
  MemberRoleSelect,
  IntegrationCard,
} from './AdminPage.styled';
import {
  useListOrgMembersQuery,
  useInviteUserMutation,
  useRemoveUserMutation,
  useChangeUserRoleMutation,
} from '@/store/usersApi';

export default function AdminPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isLoading: isAuthLoading } = useSelector((state: RootState) => state.auth);
  const isAdmin = user?.permissions?.includes('admin') ?? false;

  const { data: members, isLoading } = useListOrgMembersQuery(undefined, { skip: !isAdmin });
  const [inviteUser, { isLoading: isInviting }] = useInviteUserMutation();
  const [removeUser] = useRemoveUserMutation();
  const [changeUserRole] = useChangeUserRoleMutation();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('User');
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<{ userId: string; name: string } | null>(null);

  if (isAuthLoading) {
    return (
      <PageContent>
        <LoadingCenter>
          <LoadingSpinner size="lg" />
        </LoadingCenter>
      </PageContent>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/sprawy" replace />;
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteSuccess(false);
    const result = await inviteUser({ email, role });
    if (!isApiError(result)) {
      setEmail('');
      setInviteSuccess(true);
    }
  };

  const handleRemove = async () => {
    if (!confirmRemove) return;
    await removeUser(confirmRemove.userId);
    setConfirmRemove(null);
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    await changeUserRole({ userId, role: newRole });
  };

  const memberColumns: DataTableColumn<OrgMemberDto>[] = [
    {
      key: 'name',
      header: t('admin.table.name'),
      render: (m) => (
        <div className="d-flex align-items-center gap-2">
          {m.picture ? (
            <MemberAvatar src={m.picture} alt={m.name} />
          ) : (
            <MemberAvatarFallback>{m.name.charAt(0).toUpperCase()}</MemberAvatarFallback>
          )}
          {m.name}
        </div>
      ),
    },
    { key: 'email', header: t('admin.table.email'), render: (m) => m.email },
    {
      key: 'role',
      header: t('admin.table.role'),
      render: (m) =>
        m.userId === user?.id ? (
          <Badge bg={m.roles.includes('admin') ? 'primary' : 'secondary'}>
            {m.roles[0] ?? 'user'}
          </Badge>
        ) : (
          <MemberRoleSelect>
            <Form.Select
              size="sm"
              value={m.roles[0] ?? 'user'}
              onChange={(e) => handleRoleChange(m.userId, e.target.value)}
            >
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </Form.Select>
          </MemberRoleSelect>
        ),
    },
    {
      key: 'actions',
      header: t('admin.table.actions'),
      width: '80px',
      render: (m) =>
        m.userId !== user?.id && (
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => setConfirmRemove({ userId: m.userId, name: m.name })}
          >
            <HiTrash />
          </Button>
        ),
    },
  ];

  return (
    <PageContent>
      <PageHeader title={t('admin.title')} subtitle={t('admin.subtitle')} />

      <Section>
        <SurfaceCard>
          <SectionTitle>{t('admin.inviteTitle')}</SectionTitle>
          <Form onSubmit={handleInvite}>
            <InviteForm>
              <Form.Group className="invite-email-group">
                <Form.Label>{t('admin.emailLabel')}</Form.Label>
                <InputGroup>
                  <InputGroup.Text><HiEnvelope /></InputGroup.Text>
                  <Form.Control
                    type="email"
                    placeholder={t('admin.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </InputGroup>
              </Form.Group>
              <Form.Group className="invite-role-group">
                <Form.Label>{t('admin.roleLabel')}</Form.Label>
                <Form.Select value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                </Form.Select>
              </Form.Group>
              <Button type="submit" variant="primary" disabled={isInviting || !email}>
                {isInviting ? <Spinner size="sm" className="me-2" /> : null}
                {t('admin.inviteButton')}
              </Button>
            </InviteForm>
          </Form>
          {inviteSuccess && <Alert variant="success" className="mt-3 mb-0">{t('admin.inviteSuccess')}</Alert>}
        </SurfaceCard>
      </Section>

      <Section>
        <SurfaceCard>
          <SectionTitle>{t('admin.membersTitle')}</SectionTitle>
          {isLoading && (
            <div className="text-center py-4">
              <Spinner />
            </div>
          )}
          {members && (
            <DataTable<OrgMemberDto>
              ariaLabel={t('admin.membersTitle')}
              rowKey={(m) => m.userId}
              columns={memberColumns}
              rows={members}
              emptyMessage={t('admin.noMembers')}
            />
          )}
        </SurfaceCard>
      </Section>

      <Section>
        <StatusDefinitionsAdmin />
      </Section>

      <Section>
        <SurfaceCard>
          <IntegrationCard>
            <div>
              <h3>
                <HiKey />
                {t('portalCredentials.title')}
              </h3>
              <p>{t('portalCredentials.subtitle')}</p>
            </div>
            <Button variant="outline-primary" onClick={() => navigate('/admin/portal-credentials')}>
              {t('admin.manage', 'Manage')}
            </Button>
          </IntegrationCard>
        </SurfaceCard>
      </Section>

      <ConfirmDialog
        open={!!confirmRemove}
        title={t('admin.removeTitle')}
        message={t('admin.removeConfirm', { name: confirmRemove?.name })}
        variant="danger"
        confirmLabel={t('confirm.delete')}
        onConfirm={handleRemove}
        onCancel={() => setConfirmRemove(null)}
      />
    </PageContent>
  );
}
